import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import RealmSafeSeatsioChart from './RealmSafeSeatsioChart';
import { moveToHeldPlaces } from '../../../shared/seatsioUtils';
import { channelsForEvent } from '../../../shared/channelsByEvent';

import { ticketTypeAvailable, salesStartInFuture } from './WebOrderForm';
import CalendarEventInterestForm from './CalendarEventInterestForm';
import ConfirmCheckoutHeader from '../../shared/components/ConfirmCheckoutHeader';
import SeatMapLegend from './SeatMapLegend';
import buildLegendRows from '../utils/legendRows';
import { floorTargetFor, ALL_FLOORS } from '../utils/seatMapFocus';
import { upcomingPriceText } from '../utils/upcomingPrice';
import { onSeatMap, buildTicketTypesForSelection, addSelectedObject, applyDeselection, selectedUnits, groupSelectedObjects, pricedCategoryKeys, minimumShortfalls, sectionTicketType, buildSeatsioPricing, plainText, heldReleaseSpecs, seatsioPriceLimits } from '../utils/seatSelection';
import SeatInventoryControls from './SeatInventoryControls';
import SeatInventoryPanel from './SeatInventoryPanel';
import SeatSelectionSummary from './SeatSelectionSummary';
import { trackXPixelEvent, X_PIXEL_ADD_TO_CART } from '../utils/xPixel';

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

var _ = require('lodash');
var debouncedBuildTicketReservation;
var seatsIOObjectSelected;
var seatsIOObjectDeselected;

const ChooseSeatedTicketTypes = ({
  team,
  csrfToken,
  confirm,
  ticketTypes,
  promoCode,
  ticketTypesChanged,
  buildTicketReservation,
  visibleTicketTypes,
  addOns,
  ticketReservation,
  postToParent,
  formatItemsForGoogleTagManager,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  selectedObjectLabel,
  userContext,
  selectedObjectType,
  onSelectionChange,
  announceListEnabled,
  waitlistEnabled,
  no_orphan_seats,
  adminPreview,
  staffOrder,
  ownsUnavailableState,
  createTicketReservation,
  customerioId,
  parentUrl,
  onProceed,
  submitting,
  hideTotals
}) => {
  const [availableTickets, setAvailableTickets] = useState([]);
  const [visibleTickets, setVisibleTickets] = useState([]);
  const [scheduledTickets, setScheduledTickets] = useState([]);
  const anyVisibleTicketTypePaused = visibleTickets.some((ticketType) => ticketType.sales_paused);
  const [categoryColors, setCategoryColors] = useState({});
  const [legendCategories, setLegendCategories] = useState([]);
  const [chartReady, setChartReady] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [inventoryError, setInventoryError] = useState(null);
  const [tab, setTab] = useState('price');
  const [qty, setQty] = useState(1);
  const simple = !!team?.simple_seated_checkout;
  const [zone, setZone] = useState(null);
  const [selectionGroups, setSelectionGroups] = useState([]);
  const [viewingList, setViewingList] = useState(false);
  const currentFloorRef = useRef(null);
  const focusedCategoryRef = useRef(null);
  const categoryFloorsRef = useRef({ ...(confirm.seatsio_category_floors || {}) });
  const seatsIOSelectedObjectsRef = useRef(seatsIOSelectedObjects);
  useEffect(() => {
    seatsIOSelectedObjectsRef.current = seatsIOSelectedObjects;
  }, [seatsIOSelectedObjects]);

  const selectionGroupsRef = useRef([]);
  const applyGroups = (groups) => {
    selectionGroupsRef.current = groups;
    setSelectionGroups(groups);
  };
  const putGroup = (group) => {
    const others = selectionGroupsRef.current.filter((g) => g.key !== group.key);
    applyGroups([...others, group]);
  };
  const dropGroup = (key) => applyGroups(selectionGroupsRef.current.filter((g) => g.key !== key));

  const fanVisibleIds = adminPreview ? null : new Set(visibleTicketTypes(confirm, ticketTypes, promoCode).map((tt) => tt.id));
  const pickableTicketTypes = ticketTypes.filter((tt) => tt.price_parent_id == null || !fanVisibleIds || fanVisibleIds.has(tt.id));

  useEffect(() => {
    if ((seatsIOSelectedObjects || []).length > 0) {
      applyGroups(groupSelectedObjects(seatsIOSelectedObjects, pickableTicketTypes));
    }
  }, []);

  const seatsioSelectionValidRef = useRef(true);
  const shortfalls = adminPreview || staffOrder ? [] : minimumShortfalls(selectionGroups, ticketTypes);
  const shortfallsRef = useRef(shortfalls);
  shortfallsRef.current = shortfalls;
  const reportSelectionValidity = () => {
    if (onSelectionChange) { onSelectionChange(seatsioSelectionValidRef.current && shortfallsRef.current.length === 0, null); }
  };
  useEffect(reportSelectionValidity, [JSON.stringify(shortfalls)]);

  const handleFloorChanged = (floor) => {
    if (floor && floor.name) {
      currentFloorRef.current = floor.name;
      (floor.categories || []).forEach((category) => {
        categoryFloorsRef.current[String(category.key)] = floor.name;
      });
    } else {
      currentFloorRef.current = null;
    }
  };

  const userContextRef = useRef(userContext);
  userContextRef.current = userContext;

  useEffect(() => {
    debouncedBuildTicketReservation = _.debounce((csrfToken, confirm, updated, promoCode, addOns, ticketReservation) => {
      if (shortfallsRef.current.length > 0) { return; }
      buildTicketReservation(csrfToken, confirm, updated, promoCode, addOns, ticketReservation, userContextRef.current);
    }, 300);
  }, []);

  const typedPromoCodeName = (promoCode && !promoCode._membershipApplied && !promoCode._passApplied) ? (promoCode.name || '') : '';

  const inventoryRequestRef = useRef(0);
  const areaPartitionsRef = useRef({});
  const chartRef = useRef(seatsIOChart);
  chartRef.current = seatsIOChart;
  const heldMoveTimersRef = useRef({});
  const heldMovesInFlightRef = useRef({});
  const fetchInventoryRef = useRef(null);

  const memberAccessToken = userContext && userContext.access_token;

  const fetchInventory = () => {
    const requestId = inventoryRequestRef.current + 1;
    inventoryRequestRef.current = requestId;
    const isStale = () => requestId !== inventoryRequestRef.current;
    setInventoryLoading(true);
    const channels = channelsForEvent(confirm.seatsio_event_id, promoCode).filter((c) => c !== 'NO_CHANNEL');
    const params = channels.map((c) => `channels[]=${encodeURIComponent(c)}`);
    if (typedPromoCodeName) { params.push(`promo_code=${encodeURIComponent(typedPromoCodeName)}`); }
    if (memberAccessToken) { params.push(`ucat=${encodeURIComponent(memberAccessToken)}`); }
    const query = params.join('&');
    return fetch(`/confirms/${confirm.id}/seat_inventory${query ? `?${query}` : ''}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then((r) => {
        if (!r.ok) { throw new Error(`seat_inventory ${r.status}`); }
        return r.json();
      })
      .then((data) => {
        if (isStale()) { return; }
        const descByCategory = {};
        ticketTypes.forEach((tt) => {
          if (tt.simple_format_description && tt.price_parent_id == null) { descByCategory[String(tt.seating_chart_category_key)] = tt.simple_format_description; }
        });
        areaPartitionsRef.current = data.area_partitions || {};
        const rows = (data.inventory || []).map((row) => ({ ...row, description: descByCategory[String(row.category_key)] }));
        setInventory(rows);
        setInventoryError(null);
        setInventoryLoading(false);
      })
      .catch(() => {
        if (!isStale()) {
          setInventoryError('Could not load availability. Please try again.');
          setInventoryLoading(false);
        }
      });
  };

  const openChannelsKey = channelsForEvent(confirm.seatsio_event_id, promoCode)
    .filter((c) => c !== 'NO_CHANNEL').sort().join(',');

  useEffect(() => {
    if (!confirm.seatsio_event_id) { return undefined; }
    fetchInventory();
    return () => { inventoryRequestRef.current += 1; };
  }, [confirm.id, confirm.seatsio_event_id, openChannelsKey, typedPromoCodeName, memberAccessToken]);

  fetchInventoryRef.current = fetchInventory;
  const refetchInventory = () => fetchInventoryRef.current();

  const seatCardSubtitle = [
    confirm.support_artist_names && confirm.support_artist_names.length > 0 ? `with ${confirm.support_artist_names}` : null,
    confirm.age_restriction,
    confirm.human_date_and_time_with_day_of_week
  ].filter(Boolean).join(' · ');

  const seatCardAddress = confirm.venue && confirm.venue.has_non_country_address_info
    ? confirm.venue.address_from_components
    : null;

  const alreadySelectedUnits = (categoryKey, { seatsOnly = false } = {}) => (seatsIOSelectedObjects || [])
    .filter((o) => o.category?.key?.toString() === String(categoryKey) && !(seatsOnly && o.objectType === 'Table'))
    .reduce((sum, o) => sum + selectedUnits(o), 0);

  const pricedKeys = pricedCategoryKeys(pickableTicketTypes);
  const ticketTypeForCategory = (categoryKey) => sectionTicketType(ticketTypes, categoryKey);
  const maxTicketsForCategory = (categoryKey) => {
    const tt = ticketTypeForCategory(categoryKey);
    return (tt && tt.maximum_tickets_during_checkout) || 8;
  };
  const ticketTypeForGroup = (group) => (group.ticketTypeId != null
    ? ticketTypes.find((t) => t.id === group.ticketTypeId)
    : ticketTypeForCategory(group.categoryKey));
  const promoPricesFor = (tt) => tt && promoCode?.discounted_prices?.[tt.id];
  const originalPriceFor = (tt) => (tt && tt.calculated_price != null ? Number(tt.calculated_price) : null);
  const priceFor = (tt) => {
    const promoPrices = promoPricesFor(tt);
    return promoPrices ? Number(promoPrices.calculated_price) : originalPriceFor(tt);
  };
  const facePriceFor = (tt) => {
    const promoPrices = promoPricesFor(tt);
    if (promoPrices) { return Number(promoPrices.price); }
    return tt && tt.price != null ? Number(tt.price) : null;
  };
  const originalPriceForCategory = (categoryKey) => originalPriceFor(ticketTypeForCategory(categoryKey));
  const priceForCategory = (categoryKey) => priceFor(ticketTypeForCategory(categoryKey));
  const facePriceForCategory = (categoryKey) => facePriceFor(ticketTypeForCategory(categoryKey));
  const limitFor = (row) => {
    if (row.table) { return 1; }
    const remaining = maxTicketsForCategory(row.category_key) - alreadySelectedUnits(row.category_key);
    return Math.max(0, Math.min(row.seats, remaining));
  };

  const allInPricing = !!confirm.enable_all_in_pricing;
  const showFeeBreakdown = confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false;

  const gaSelectionFor = (row, amount) => {
    const units = row.ga_units && row.ga_units.length ? row.ga_units : [{ label: row.labels[0], units: amount }];
    const taken = new Set((seatsIOSelectedObjectsRef.current || []).map((o) => o.label));
    const picks = [];
    let remaining = amount;
    const ordered = [...units.filter((u) => u.whole).sort((a, b) => b.units - a.units), ...units.filter((u) => !u.whole)];
    ordered.forEach((u) => {
      if (remaining <= 0) { return; }
      if (u.whole) {
        if (u.units <= remaining && !taken.has(u.label)) { picks.push({ label: u.label, amount: u.units }); remaining -= u.units; }
        return;
      }
      const take = Math.min(remaining, u.units);
      if (take > 0) { picks.push({ label: u.label, amount: take }); remaining -= take; }
    });
    return picks;
  };

  const wholeSizesFor = (row) => [...new Set((row.ga_units || []).filter((u) => u.whole).map((u) => u.units))].sort((a, b) => a - b);
  const allWhole = (row) => (row.ga_units || []).length > 0 && row.ga_units.every((u) => u.whole);

  const nextWholeUnit = (group) => {
    const row = inventory.find((r) => r.ga && String(r.category_key) === String(group.categoryKey));
    const taken = new Set([...(seatsIOSelectedObjectsRef.current || []).map((o) => o.label), ...group.labels]);
    return ((row && row.ga_units) || []).filter((u) => u.whole && !taken.has(u.label)).sort((a, b) => a.units - b.units)[0];
  };

  const sameLabel = (a, b) => String(a ?? '') === String(b ?? '');

  const heldInRow = (row) => (seatsIOSelectedObjectsRef.current || [])
    .filter((o) => o.objectType === 'Seat')
    .filter((o) => sameLabel(o.category?.key, row.category_key) && sameLabel(o.labels?.section, row.section))
    .filter((o) => (row.tables ? row.tables.some((t) => sameLabel(t.row, o.labels?.parent)) : sameLabel(o.labels?.parent, row.row)))
    .map((o) => o.label);

  const selectSpecFor = (row, amount) => {
    if (row.ga) { return gaSelectionFor(row, amount); }
    if (row.table) { return [row.labels[0]]; }
    const held = heldInRow(row);
    const heldTables = (row.tables || []).filter((t) => t.labels.some((label) => held.includes(label)));
    const ordered = [...new Set([...heldTables.flatMap((t) => t.labels), ...row.labels])];
    return ordered.filter((label) => !held.includes(label)).slice(0, amount);
  };

  const groupFromInventoryRow = (row, qty, picks) => {
    if (row.ga) {
      const group = { key: `ga|${row.category_key}`, name: row.name, categoryKey: String(row.category_key), section: null, row: null, ga: true, table: false, qty, labels: [row.labels[0]] };
      if (!allWhole(row)) { return group; }
      return { ...group, labels: picks.map((p) => p.label), wholeSizes: Object.fromEntries(picks.map((p) => [p.label, p.amount])) };
    }
    if (row.table) {
      return { key: `table|${row.labels[0]}`, name: row.name, categoryKey: String(row.category_key), section: row.section, row: null, ga: false, table: true, qty: 1, labels: [row.labels[0]] };
    }
    const labels = [...heldInRow(row), ...picks];
    return { key: `${row.category_key}|${row.section || ''}|${row.row || ''}`, name: row.name, categoryKey: String(row.category_key), section: row.section, row: row.row, ga: false, table: false, qty: labels.length, labels };
  };

  const availableSeatsForGroup = (group) => {
    const taken = new Set([...(seatsIOSelectedObjectsRef.current || []).map((o) => o.label), ...group.labels]);
    return inventory
      .filter((r) => !r.ga && !r.table && String(r.category_key) === String(group.categoryKey) && r.section === group.section)
      .flatMap((r) => (r.tables || [r]).filter((t) => t.row === group.row))
      .flatMap((t) => t.labels || [])
      .filter((l) => !taken.has(l));
  };

  const groupLimit = (group) => {
    if (group.table) { return 1; }
    const byCategory = maxTicketsForCategory(group.categoryKey) - Math.max(0, alreadySelectedUnits(group.categoryKey) - group.qty);
    const priceTicketType = group.ticketTypeId != null ? ticketTypeForGroup(group) : null;
    const byPrice = priceTicketType && priceTicketType.maximum_tickets_during_checkout
      ? priceTicketType.maximum_tickets_during_checkout - Math.max(0, (priceTicketType.quantity || 0) - group.qty)
      : byCategory;
    const cap = Math.min(byCategory, byPrice);
    if (group.ga) { return Math.max(1, cap); }
    return Math.max(group.qty, Math.min(group.qty + availableSeatsForGroup(group).length, cap));
  };

  const freeWholeUnitsCovering = (row, needed) => {
    const taken = new Set((seatsIOSelectedObjectsRef.current || []).map((o) => o.label));
    const free = row.ga_units.filter((u) => !taken.has(u.label)).map((u) => u.units).sort((a, b) => a - b);
    if (free.length === 0) { return wholeSizesFor(row)[0]; }
    const single = free.find((units) => units >= needed);
    if (single) { return single; }
    let total = 0;
    free.slice().reverse().some((units) => { total += units; return total >= needed; });
    return total;
  };

  const onSelectRow = async (row) => {
    if (!chartReady || Object.keys(seatsIOChart).length === 0) { return; }
    const sectionMinimum = adminPreview || staffOrder ? 0 : Number(ticketTypeForCategory(row.category_key)?.minimum_tickets_per_order) || 0;
    const held = row.ga || row.table ? 0 : heldInRow(row).length;
    const stillNeeded = sectionMinimum - alreadySelectedUnits(row.category_key, { seatsOnly: true });
    const wanted = !simple ? qty : row.ga && allWhole(row) ? freeWholeUnitsCovering(row, Math.max(1, stillNeeded)) : held + 1;
    const amount = Math.min(Math.max(wanted - held, sectionMinimum - alreadySelectedUnits(row.category_key, { seatsOnly: true })), limitFor(row));
    if (amount <= 0 && held > 0) {
      const keep = Math.max(wanted, held - (alreadySelectedUnits(row.category_key, { seatsOnly: true }) - sectionMinimum));
      await releaseExtras(heldInRow(row).slice(keep));
      setViewingList(false);
      focusCategory(row.zone);
      return;
    }
    if (amount <= 0) { return; }
    const spec = selectSpecFor(row, amount);
    const wholeSizes = row.ga ? wholeSizesFor(row) : [];
    if (wholeSizes.length > 0 && spec.reduce((sum, pick) => sum + pick.amount, 0) < amount) {
      const overLimit = wholeSizes[0] > limitFor(row);
      setInventoryError(`${row.name} is sold as a whole (${wholeSizes.join(' or ')} tickets)${overLimit ? ', more than this order allows' : ''}.`);
      return;
    }
    setInventoryError(null);
    const group = groupFromInventoryRow(row, amount, spec);
    if (!pricedKeys.has(String(row.category_key))) { putGroup(group); }
    setViewingList(false);
    focusCategory(row.zone);
    try {
      await seatsIOChart.trySelectObjects(spec);
    } catch (e) {
      applyGroups(groupSelectedObjects(seatsIOSelectedObjectsRef.current, pickableTicketTypes));
      focusCategory(null);
      await refetchInventory();
      setInventoryError('Those seats were just taken. Please choose again.');
    }
  };

  const gaDeselectSpec = (group, total) => {
    const selected = (seatsIOSelectedObjectsRef.current || []).filter((o) => group.labels.includes(o.label));
    const ordered = [...selected.filter((o) => !o.label.includes('##')), ...selected.filter((o) => o.label.includes('##'))];
    let remaining = total;
    return ordered.reduce((spec, o) => {
      const amount = Math.min(remaining, selectedUnits(o));
      remaining -= amount;
      return amount > 0 ? [...spec, { label: o.label, amount }] : spec;
    }, []);
  };

  const changeWholeGroupQty = async (group, next) => {
    if (next > group.qty) {
      const unit = nextWholeUnit(group);
      if (!unit) { return; }
      await seatsIOChart.trySelectObjects([withPrice(group, { label: unit.label, amount: unit.units })]);
      const current = selectionGroupsRef.current.find((g) => g.key === group.key) || group;
      if (current.labels.includes(unit.label)) { return; }
      putGroup({ ...current, qty: current.qty + unit.units, labels: [...current.labels, unit.label], wholeSizes: { ...current.wholeSizes, [unit.label]: unit.units } });
    } else {
      const label = group.labels[group.labels.length - 1];
      const size = group.wholeSizes[label];
      const { [label]: _removed, ...wholeSizes } = group.wholeSizes;
      const held = group.ticketTypeId != null && heldObject(label);
      await seatsIOChart.deselectObjects(held ? heldReleaseSpecs(held, pickableTicketTypes, group.ticketTypeId, size) : [{ label, amount: size }]);
      putGroup({ ...group, qty: group.qty - size, labels: group.labels.slice(0, -1), wholeSizes });
    }
  };

  const withPrice = (group, spec) => (group.ticketTypeId != null ? { ...spec, ticketType: String(group.ticketTypeId) } : spec);
  const seatSpecs = (group, labels) => (group.ticketTypeId != null ? labels.map((label) => withPrice(group, { label })) : labels);
  const heldObject = (label) => seatsIOSelectedObjectsRef.current.find((o) => o.label === label);
  const releaseSpecs = (group, labels, amount) => {
    if (!pricedCategoryKeys(ticketTypes).has(String(group.categoryKey))) {
      return group.ga ? gaDeselectSpec(group, amount) : labels;
    }
    if (group.ga) {
      let left = amount;
      return labels.flatMap((label) => {
        if (left <= 0) { return []; }
        const object = heldObject(label);
        const specs = object ? heldReleaseSpecs(object, pickableTicketTypes, group.ticketTypeId, left) : [{ label, amount: left }];
        left -= specs.reduce((sum, spec) => sum + spec.amount, 0);
        return specs;
      });
    }
    return labels.map((label) => {
      const held = heldObject(label)?.selectedTicketType;
      return held && held !== 'NO_TICKET_TYPE' ? { label, ticketType: String(held) } : label;
    });
  };

  const releaseExtras = async (extras) => {
    const groups = selectionGroupsRef.current.filter((g) => g.labels.some((label) => extras.includes(label)));
    await Promise.all(groups.map(async (group) => {
      const removed = group.labels.filter((label) => extras.includes(label));
      const kept = group.labels.filter((label) => !extras.includes(label));
      await seatsIOChart.deselectObjects(releaseSpecs(group, removed));
      if (kept.length === 0) { dropGroup(group.key); return; }
      putGroup({ ...group, qty: kept.length, labels: kept });
    }));
  };

  const changeGroupQty = async (group, next) => {
    const prev = group.qty;
    if (next === prev || group.table || !chartReady || Object.keys(seatsIOChart).length === 0) { return; }
    try {
      if (group.ga && group.wholeSizes) {
        await changeWholeGroupQty(group, next);
      } else if (next > prev) {
        if (group.ga) {
          const area = group.labels[0].split('##')[0];
          const partitionId = areaPartitionsRef.current[area];
          const add = [withPrice(group, { label: area, amount: next - prev })];
          await (partitionId
            ? seatsIOChart.trySelectObjects([withPrice(group, { label: partitionId, amount: next - prev })]).catch(() => seatsIOChart.trySelectObjects(add))
            : seatsIOChart.trySelectObjects(add));
        } else {
          const added = availableSeatsForGroup(group).slice(0, next - prev);
          if (added.length === 0) { return; }
          await seatsIOChart.trySelectObjects(seatSpecs(group, added));
          putGroup({ ...group, qty: group.qty + added.length, labels: [...group.labels, ...added] });
        }
      } else if (group.ga) {
        await seatsIOChart.deselectObjects(releaseSpecs(group, group.labels, prev - next));
      } else {
        const removed = group.labels.slice(next);
        await seatsIOChart.deselectObjects(releaseSpecs(group, removed));
        putGroup({ ...group, qty: next, labels: group.labels.slice(0, next) });
      }
    } catch (e) {
      putGroup(group);
      if (group.wholeSizes) {
        await refetchInventory();
        setInventoryError('That area was just taken. Please choose again.');
      }
    }
  };

  const removeGroup = async (group) => {
    dropGroup(group.key);
    if (chartReady && Object.keys(seatsIOChart).length > 0) {
      await seatsIOChart.deselectObjects(releaseSpecs(group, group.labels, group.qty)).catch(() => {});
    }
    if (selectionGroupsRef.current.length === 0) { focusCategory(null); }
  };

  const reserveDetail = () => {
    if (selectionGroupsRef.current.length === 0) { return; }
    onProceed();
  };

  const backToList = () => {
    setViewingList(true);
    focusCategory(null);
  };

  const cartFor = (selected) => buildTicketTypesForSelection(ticketTypes, selected, {
    selectedObjectLabel,
    selectedObjectType,
    configuredChannels: channelsForEvent(confirm.seatsio_event_id, promoCode),
    priceOptions: pickableTicketTypes,
  });

  const pickableKey = pickableTicketTypes.map((tt) => tt.id).join(',');
  const pickableKeyRef = useRef(pickableKey);
  useEffect(() => {
    if (pickableKeyRef.current === pickableKey) { return; }
    pickableKeyRef.current = pickableKey;
    const selected = seatsIOSelectedObjectsRef.current;
    if (selected.length === 0) { return; }
    const updatedTicketTypes = cartFor(selected);
    ticketTypesChanged(updatedTicketTypes);
    debouncedBuildTicketReservation(csrfToken, confirm, updatedTicketTypes, promoCode, addOns, ticketReservation);
    applyGroups(groupSelectedObjects(selected, pickableTicketTypes));
  }, [pickableKey]);

  useEffect(() => {
    seatsIOObjectSelected = (object, selectedTicketType) => {
      const updated = addSelectedObject(seatsIOSelectedObjectsRef.current, object);
      seatsIOSelectedObjectsRef.current = updated;

      const updatedTicketTypes = cartFor(updated);

      seatsIOSelectedObjectsChanged(updated);
      ticketTypesChanged(updatedTicketTypes);
      debouncedBuildTicketReservation(csrfToken, confirm, updatedTicketTypes, promoCode, addOns, ticketReservation);

      applyGroups(groupSelectedObjects(updated, pickableTicketTypes));
      setViewingList(false);

      if (areaPartitionsRef.current[object.id] && !heldMovesInFlightRef.current[object.id] && !pricedKeys.has(String(object.category?.key))) {
        clearTimeout(heldMoveTimersRef.current[object.id]);
        heldMoveTimersRef.current[object.id] = setTimeout(() => {
          heldMovesInFlightRef.current[object.id] = true;
          moveToHeldPlaces(chartRef.current, object, areaPartitionsRef.current)
            .finally(() => { heldMovesInFlightRef.current[object.id] = false; });
        }, 300);
      }

      const updatedItem = (selectedTicketType && updatedTicketTypes.find((ticketType) => String(ticketType.id) === String(selectedTicketType.ticketType)))
        || sectionTicketType(updatedTicketTypes, object.category.key.toString());
      const items = formatItemsForGoogleTagManager(confirm, promoCode, [updatedItem], []);
      postToParent('ga.add_to_cart', items);
      trackXPixelEvent(X_PIXEL_ADD_TO_CART, confirm, {
        value: updatedItem.calculated_price,
        numItems: 1,
      });
    };

    seatsIOObjectDeselected = (object) => {
      if (!(Number(object.numSelected) > 0)) { clearTimeout(heldMoveTimersRef.current[object.id]); }
      const updated = applyDeselection(seatsIOSelectedObjectsRef.current, object);
      seatsIOSelectedObjectsRef.current = updated;

      const updatedTicketTypes = cartFor(updated);

      seatsIOSelectedObjectsChanged(updated);
      ticketTypesChanged(updatedTicketTypes);
      debouncedBuildTicketReservation(csrfToken, confirm, updatedTicketTypes, promoCode, addOns, ticketReservation);

      applyGroups(groupSelectedObjects(updated, pickableTicketTypes));
      if (updated.length === 0) { focusCategory(null); }
    };
  }, [seatsIOSelectedObjects, csrfToken, confirm, ticketTypes, promoCode, addOns, ticketReservation, inventory]);

  useEffect(() => {
    setVisibleTickets(
      adminPreview ? ticketTypes : visibleTicketTypes(confirm, ticketTypes, promoCode)
    );
  }, [confirm, ticketTypes, promoCode, visibleTicketTypes, adminPreview])

  useEffect(() => {
    setAvailableTickets(
      adminPreview ? visibleTickets : visibleTickets.filter((ticketType) => ticketTypeAvailable(ticketType))
    );

    setScheduledTickets(
      visibleTickets
        .filter((ticketType) => ticketType.scheduled)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    );
  }, [visibleTickets, adminPreview]);

  const cheapestOffered = (categoryKey) => _.minBy(availableTickets.filter((tt) => String(tt.seating_chart_category_key) === String(categoryKey) && priceFor(tt) != null), priceFor);
  const popoverInfoRef = useRef(null);
  popoverInfoRef.current = (object) => {
    if(!object.forSale){
      return null;
    }
    if(object.category && pricedKeys.has(object.category.key.toString())){
      const cheapest = cheapestOffered(object.category.key);
      return cheapest ? [`From ${formatter.format(priceFor(cheapest))}`, promoPricesFor(cheapest) ? null : upcomingPriceText(cheapest, formatter)].filter(Boolean).join('[br/]') : null;
    }
    if(!object.category){
      return null;
    }

    var ticketType = sectionTicketType(availableTickets, object.category.key.toString()) || sectionTicketType(visibleTicketTypes(confirm, ticketTypes, promoCode), object.category.key.toString());
    var showFees = confirm.enable_all_in_pricing && confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false;

    return [showFees ? ticketType?.all_in_price_description : null, promoPricesFor(ticketType) ? null : upcomingPriceText(ticketType, formatter)].filter(Boolean).join('[br/]') || null;
  };

  const listedInventory = inventory.map((row) => {
    if (row.price == null) { return row; }
    const cheapest = cheapestOffered(row.category_key);
    if (!cheapest) { return row; }
    return { ...row, price: priceFor(cheapest), original_price: promoPricesFor(cheapest) ? originalPriceFor(cheapest) : null, from_price: pricedKeys.has(String(row.category_key)), upcoming: promoPricesFor(cheapest) ? null : upcomingPriceText(cheapest, formatter) };
  });
  const maxAvailablePrice = Math.ceil(
    listedInventory.reduce((max, row) => (row.price != null && Number(row.price) > max ? Number(row.price) : max), 0)
  );

  const pricingKey = JSON.stringify(buildSeatsioPricing(pickableTicketTypes, availableTickets, promoPricesFor));

  useEffect(() => {
    if(Object.keys(seatsIOChart).length > 0){
      seatsIOChart.changeConfig({
        pricing: JSON.parse(pricingKey)
      });
    }
  }, [seatsIOChart, pricingKey]);

  const maxSelectedObjects = ticketTypes.filter((tt) => onSeatMap(tt) && tt.price_parent_id == null).map((tt) => {
    return {
      category: tt.seating_chart_category_key,
      quantity: tt.maximum_tickets_during_checkout
    };
  }).concat(seatsioPriceLimits(pickableTicketTypes));
  const maxSelectedObjectsKey = JSON.stringify(maxSelectedObjects);

  useEffect(() => {
    if(Object.keys(seatsIOChart).length > 0){
      seatsIOChart.changeConfig({
        maxSelectedObjects: JSON.parse(maxSelectedObjectsKey)
      });
    }
  }, [seatsIOChart, maxSelectedObjectsKey]);

  useEffect(() => {
    if(Object.keys(seatsIOChart).length > 0){
      var now = new Date();

      var visibleCategoryIds = visibleTicketTypes(confirm, ticketTypes, promoCode)
        .filter(onSeatMap)
        .filter((tt) => {
          var startTime = new Date(tt.start_time);
          var endTime = new Date(tt.end_time);

          return (startTime <= now && now <= endTime && !tt.sales_paused);
        })
        .map((tt) => tt.seating_chart_category_key);

      // Empty array means everything is available
      if(visibleCategoryIds.length === 0){
        var mockCategoryId = uuidv4();
        visibleCategoryIds.push(mockCategoryId);
      }

      seatsIOChart.changeConfig({
        availableCategories: visibleCategoryIds
      });
    }
  }, [seatsIOChart, visibleTicketTypes, ticketTypes, promoCode]);

  const hasNothingAvailable = () => {
    if (adminPreview) return false;
    if (confirm.seated_hold_present) return false;

    return availableTickets.length === 0;
  };

  const showUnavailableState = () => ownsUnavailableState && hasNothingAvailable();

  const focusCategory = async (categoryKey) => {
    if(!chartReady || Object.keys(seatsIOChart).length === 0){
      return;
    }

    focusedCategoryRef.current = categoryKey;

    await seatsIOChart.changeConfig({ filteredCategories: categoryKey ? [categoryKey] : [] });

    if(!categoryKey){
      seatsIOChart.resetView();
      return;
    }

    const target = floorTargetFor(categoryFloorsRef.current, currentFloorRef.current, categoryKey);

    if(target === ALL_FLOORS && typeof seatsIOChart.goToAllFloorsView === "function"){
      await seatsIOChart.goToAllFloorsView().catch(() => {});
    } else if(target && typeof seatsIOChart.goToFloor === "function"){
      await seatsIOChart.goToFloor(target).catch(() => {});
    }

    seatsIOChart.zoomToFilteredCategories();
  };

  const previewCategory = (categoryKey) => {
    if(!chartReady || Object.keys(seatsIOChart).length === 0){
      return;
    }

    const shown = categoryKey || focusedCategoryRef.current;
    Promise.resolve(seatsIOChart.changeConfig({ filteredCategories: shown ? [shown] : [] })).catch(() => {});
  };

  const showCard = !showUnavailableState() && !hasNothingAvailable()
    && confirm.seatsio_event_id && confirm.seatsio_event_id.length > 0;

  return (
    <div>
      {showCard ? null : <ConfirmCheckoutHeader confirm={confirm} />}
      {showUnavailableState() ? (
        confirm.preselling ? null : salesStartInFuture(confirm) ? (
          announceListEnabled ? (
            <CalendarEventInterestForm
              confirm={confirm}
              csrfToken={csrfToken}
              interestType="Announce"
            />
          ) : (
            <div className="card border-0 h-100" style={{"background": "#f9f9f9"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                    style={{"fontSize": "16px"}}>
                    <strong>Coming soon!</strong>
                  </p>
                  <p className="mb-0">
                    Sales not started yet.
                  </p>
                </div>
              </div>
            </div>
          )
        ) : (
          waitlistEnabled && !anyVisibleTicketTypePaused ? (
            <CalendarEventInterestForm
              confirm={confirm}
              csrfToken={csrfToken}
              interestType="Waitlist"
            />
          ) : (
            <div className="card border-0 h-100"
                  style={{"background": "#f9f9f9"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                      style={{"fontSize": "16px"}}>
                    <strong>Tickets not currently available</strong>
                  </p>
                  {anyVisibleTicketTypePaused ? (
                    <p className="mb-0">
                      Online sales for this event are paused.
                    </p>
                  ) : scheduledTickets.length > 0 ? (
                    <>
                      <p className="mb-0">
                        Online sales for this event are currently not available.
                      </p>
                      <p className="mb-0">
                        {`The next sale will begin on ${scheduledTickets[0].human_start_time}.`}
                      </p>
                    </>
                  ) : (
                    <p className="mb-0">
                      Online sales for this event have ended.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        )
      ) : hasNothingAvailable() ? null
        : confirm.seatsio_event_id && confirm.seatsio_event_id.length > 0 ? (
        <div className="seat-inventory seat-map-shell seat-two-pane-card">
          <div className="seat-card-header">
            <div className="seat-card-info">
              {confirm.compressed_main_event_image_url ? (
                <img src={confirm.compressed_main_event_image_url} className="seat-card-image rounded" alt="" />
              ) : null}
              <div className="seat-card-title">
                <div className="seat-card-title-name">{confirm.title}</div>
                {seatCardSubtitle ? (
                  <div className="seat-card-title-sub">{seatCardSubtitle}</div>
                ) : null}
                {seatCardAddress ? (
                  <div className="seat-card-title-sub">{seatCardAddress}</div>
                ) : null}
                {confirm.venue && confirm.venue.location_notice ? (
                  <div className="seat-card-title-sub">{confirm.venue.location_notice}</div>
                ) : null}
              </div>
            </div>
            {simple ? null : (
              <SeatInventoryControls
                qty={qty}
                onQtyChange={setQty}
              />
            )}
          </div>
          <div className="row mt-2 mb-0">
          <div className="col-12 col-lg-7">
            <div className="seat-map-card">
              <SeatMapLegend rows={buildLegendRows(visibleTickets.filter(onSeatMap), availableTickets, categoryColors)}
                             categories={legendCategories}
                             onFocusCategory={focusCategory}
                             disabled={!chartReady} />
              <div className="seat-map-chart">
                  <RealmSafeSeatsioChart workspaceKey={seatsIOPublicKey}
                                   event={confirm.seatsio_event_id}
                                   channels={channelsForEvent(
                                     confirm.seatsio_event_id,
                                     promoCode
                                   )}
                                   fitTo={'widthAndHeight'}
                                   mode={adminPreview ? 'static' : undefined}
                                   session={adminPreview ? 'none' : undefined}
                                   onRenderStarted={
                                     chart => {
                                       seatsIOChartChanged(chart);

                                       if(!adminPreview){
                                         chart.config.session = "start";
                                         chart.config.onObjectSelected = (object, selectedTicketType) => seatsIOObjectSelected(object, selectedTicketType);
                                         chart.config.onObjectDeselected = (object) => seatsIOObjectDeselected(object);
                                         chart.config.onObjectClicked = (object) => setZone(object?.category?.key?.toString() || null);
                                       }
                                       chart.config.selectionValidators = [
                                        ...(no_orphan_seats ? [{ type: 'noOrphanSeats' }] : [])
                                      ];
                                      chart.config.onSelectionValid = () => {
                                        seatsioSelectionValidRef.current = true;
                                        reportSelectionValidity();
                                      };
                                      chart.config.onSelectionInvalid = errors => {
                                        seatsioSelectionValidRef.current = false;
                                        onSelectionChange(false, errors);
                                      };
                                      chart.config.onFloorChanged = (floor) => handleFloorChanged(floor);
                                     }
                                   }
                                   onChartRendered={
                                     chart => {
                                       setChartReady(true);

                                       if(typeof chart.listCategories === "function"){
                                         chart.listCategories().then(categories => {
                                           setCategoryColors(
                                             (categories || []).reduce((acc, category) => {
                                               acc[String(category.key)] = category.color;
                                               return acc;
                                             }, {})
                                           );
                                           const now = new Date();
                                           const visibleKeys = new Set(
                                             visibleTicketTypes(confirm, ticketTypes, promoCode)
                                               .filter((tt) => new Date(tt.start_time) <= now && now <= new Date(tt.end_time) && !tt.sales_paused)
                                               .map((tt) => String(tt.seating_chart_category_key))
                                           );
                                           setLegendCategories(
                                             (categories || [])
                                               .filter((category) => visibleKeys.size === 0 || visibleKeys.has(String(category.key)))
                                               .map((category) => ({
                                                 key: String(category.key),
                                                 label: category.label,
                                                 color: category.color
                                               }))
                                           );
                                         });
                                       }
                                     }
                                   }
                                   popoverInfo={(object) => popoverInfoRef.current(object)}
                                   priceFormatter={
                                     price => formatter.format(price)
                                   }
                                   region="na" />
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-5 seat-panel-col">
            {inventoryError ? <div className="text-danger small mb-2">{inventoryError}</div> : null}
            {selectionGroups.length > 0 && !viewingList ? (
              <SeatSelectionSummary
                groups={selectionGroups.map((g) => ({
                  id: g.key,
                  group: g,
                  name: g.name,
                  qty: g.qty,
                  limit: groupLimit(g),
                  step: g.wholeSizes ? g.wholeSizes[g.labels[g.labels.length - 1]] : 1,
                  stepUp: g.wholeSizes ? (nextWholeUnit(g)?.units ?? Infinity) : 1,
                  unitPrice: priceFor(ticketTypeForGroup(g)),
                  originalUnitPrice: promoPricesFor(ticketTypeForGroup(g)) ? originalPriceFor(ticketTypeForGroup(g)) : null,
                  facePrice: facePriceFor(ticketTypeForGroup(g)),
                  description: g.ticketTypeId != null && plainText(ticketTypeForGroup(g)?.simple_format_description) ? ticketTypeForGroup(g).simple_format_description : null,
                  color: categoryColors[g.categoryKey] || categoryColors[String(g.categoryKey)],
                  table: !!g.table,
                }))}
                offMapLines={ticketTypes
                  .filter((tt) => !onSeatMap(tt) && Number(tt.quantity) > 0)
                  .map((tt) => ({
                    id: tt.id,
                    name: tt.name,
                    qty: Number(tt.quantity),
                    unitPrice: priceFor(tt),
                    originalUnitPrice: promoPricesFor(tt) ? originalPriceFor(tt) : null,
                    facePrice: facePriceFor(tt),
                  }))}
                hideTotals={hideTotals}
                allInPricing={allInPricing}
                showFeeBreakdown={showFeeBreakdown}
                reserving={submitting}
                blockedReasons={shortfalls}
                onChangeQty={changeGroupQty}
                onRemove={removeGroup}
                onReserve={reserveDetail}
                onBack={backToList}
              />
            ) : (
              <>
                {selectionGroups.length > 0 ? (
                  <button
                    type="button"
                    className="seat-inventory-review-bar"
                    onClick={() => setViewingList(false)}
                  >
                    <span>{`Review selection (${selectionGroups.reduce((sum, g) => sum + g.qty, 0)})`}</span>
                    <span className="seat-inventory-review-bar-arrow" aria-hidden="true">→</span>
                  </button>
                ) : null}
              <SeatInventoryPanel
                inventory={listedInventory}
                tab={tab}
                qty={qty}
                maxPrice={maxAvailablePrice}
                zone={zone}
                colorByZone={categoryColors}
                allInPricing={allInPricing}
                simple={simple}
                onTabChange={(t) => { setTab(t); if (zone) { setZone(null); focusCategory(null); } }}
                onSelectRow={onSelectRow}
                onHoverRow={previewCategory}
                onZoneSelect={(z) => { setZone(z); focusCategory(z); }}
                onClearZone={() => { setZone(null); focusCategory(null); }}
                loading={inventoryLoading}
              />
              </>
            )}
          </div>
          </div>
        </div>
      ) : (
        <div className="row my-2">
          <div className="col-12">
            <div className="card border-0 h-100"
                 style={{"background": "#f9f9f9"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                     style={{"fontSize": "16px"}}>
                    <strong>An error occurred.</strong>
                  </p>
                  <p className="mb-0">
                    Please try again later.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ChooseSeatedTicketTypes.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  ticketTypesChanged: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  visibleTicketTypes: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string.isRequired,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  selectedObjectLabel: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  selectedObjectType: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  no_orphan_seats: PropTypes.bool.isRequired,
  adminPreview: PropTypes.bool,
  staffOrder: PropTypes.bool,
  ownsUnavailableState: PropTypes.bool,
  createTicketReservation: PropTypes.func,
  customerioId: PropTypes.string,
  parentUrl: PropTypes.string,
  onProceed: PropTypes.func,
  submitting: PropTypes.bool,
  hideTotals: PropTypes.bool
};

ChooseSeatedTicketTypes.defaultProps = {
  adminPreview: false,
  staffOrder: false,
  ownsUnavailableState: true,
  hideTotals: false
};

export default ChooseSeatedTicketTypes;
